import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { useAgentCustomAllocation } from './useAgentCustomAllocation';

// TODO: cleanup with cp_annotation_createproject_agent ff

// TODO: no VDS token for these column widths — needs designer input. The percent width matches
// the legacy AllocationConfiguration input.
const PERCENT_COLUMN_WIDTH = 100;
const ENCOUNTERS_COLUMN_WIDTH = 140;
const TOTAL_ERROR_ID = 'agent-custom-allocation-total-error';
const NO_COUNT = '—';

interface AgentCustomAllocationProps {
  /** n: the agent's encounters, and the human encounters the percentages split. */
  encounterCount: number;
}

/**
 * Step 6 in Custom mode with the annotation agent on: the agent's fixed n first, then a whole
 * percentage per human with the encounters it gives. Laid out like `AllocationConfiguration`,
 * the per-split table used without the agent.
 */
export const AgentCustomAllocation = ({ encounterCount }: AgentCustomAllocationProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { rows, total, isValid, changeShare } = useAgentCustomAllocation(encounterCount);
  const isTotalInvalid = total !== 100;

  const rowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    px: theme.spacing(4),
    borderTop: `1px solid ${theme.palette.divider}`,
  };
  const valueCells = (percent: ReactNode, encounters: ReactNode) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
      <Box role="cell" sx={{ width: PERCENT_COLUMN_WIDTH, py: theme.spacing(2) }}>
        {percent}
      </Box>
      <Box role="cell" sx={{ width: ENCOUNTERS_COLUMN_WIDTH, textAlign: 'right' }}>
        {encounters}
      </Box>
    </Box>
  );
  const encountersLabel = (count: number | null) =>
    count === null
      ? NO_COUNT
      : formatMessage(
          { id: 'createProjectWizard.step.allocation.agent.custom.encounterCount' },
          { encounterCount: count }
        );

  return (
    <Box id="agent-custom-allocation" sx={{ mb: theme.spacing(4) }}>
      <Box
        role="table"
        aria-label={formatMessage({
          id: 'createProjectWizard.step.allocation.allocationMode.customAllocation',
        })}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${borderRadius.md}px`,
        }}
      >
        <Box
          role="row"
          id="table-header"
          sx={{ display: 'flex', justifyContent: 'space-between', p: theme.spacing(4) }}
        >
          <Typography role="columnheader" variant="body1" sx={{ fontWeight: 'medium' }}>
            {formatMessage({ id: 'createProjectWizard.step.allocation.agent.custom.annotator' })}
          </Typography>
          <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
            <Typography
              role="columnheader"
              variant="body1"
              sx={{ fontWeight: 'medium', width: PERCENT_COLUMN_WIDTH }}
            >
              {formatMessage({ id: 'createProjectWizard.step.allocation.agent.custom.percent' })}
            </Typography>
            <Typography
              role="columnheader"
              variant="body1"
              sx={{ fontWeight: 'medium', width: ENCOUNTERS_COLUMN_WIDTH, textAlign: 'right' }}
            >
              {formatMessage({
                id: 'createProjectWizard.step.allocation.agent.custom.encounters',
              })}
            </Typography>
          </Box>
        </Box>

        <Box id="table-body">
          <Box
            role="row"
            id="agent-row"
            sx={{ ...rowSx, backgroundColor: theme.palette.grey[100] }}
          >
            <Box role="cell" sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              <SmartToyOutlinedIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="body1">
                {formatMessage({ id: 'createProjectWizard.step.allocation.agent.label' })}
              </Typography>
            </Box>
            {valueCells(
              null,
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {encountersLabel(encounterCount)}
              </Typography>
            )}
          </Box>
          {rows.map((row) => (
            <Box role="row" key={row.id} sx={rowSx}>
              <Typography role="cell" variant="body1">
                {row.label}
              </Typography>
              {valueCells(
                <TextField
                  value={row.rawShare}
                  onChange={(event) => changeShare(row.id, event.target.value)}
                  onFocus={(event) => event.target.select()}
                  type="text"
                  size="small"
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      'aria-label': formatMessage(
                        { id: 'createProjectWizard.step.allocation.agent.custom.percentInput' },
                        { name: row.label }
                      ),
                      'aria-describedby': isTotalInvalid ? TOTAL_ERROR_ID : undefined,
                    },
                  }}
                  sx={{ width: PERCENT_COLUMN_WIDTH }}
                />,
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  {encountersLabel(row.encounterCount)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        <Box
          role="row"
          id="table-footer"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(4),
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography role="cell" variant="body1">
            {formatMessage({ id: 'createProjectWizard.step.allocation.agent.custom.total' })}
          </Typography>
          <Typography
            role="cell"
            variant="body1"
            sx={{
              fontWeight: 'bold',
              color: isValid ? theme.palette.success.main : theme.palette.error.main,
            }}
          >
            {`${total}%`}
          </Typography>
        </Box>
      </Box>

      {isTotalInvalid && (
        <Typography
          id={TOTAL_ERROR_ID}
          variant="body2"
          sx={{ color: 'error.main', mt: theme.spacing(1) }}
        >
          {formatMessage(
            { id: 'createProjectWizard.step.allocation.agent.custom.totalError' },
            { total }
          )}
        </Typography>
      )}
    </Box>
  );
};
